import { message } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { ENDPOINTS } from '@sportninja/common/api/endpoints'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { t } from '@sportninja/common/i18n'
import type { SeedPlacement } from '@sportninja/common/features/competition/operations/utils/resolveSeedPlacement'

const translate = t as (key: string, options?: unknown) => string

interface MoveArgs {
  teamId: string
  /** The division the team is leaving — part of the endpoint path. */
  fromScheduleId: string
  toScheduleId: string
  /**
   * Where the team lands among its new siblings. Omitted by the move MODAL,
   * which has no ordering UI — the server then appends, matching legacy's
   * default of `seed_action: 'after'` with no destination team.
   */
  placement?: SeedPlacement
  /**
   * Silences the success toast. A DRAG is self-evidencing — the row is already
   * sitting where it was dropped — so a toast on top of it is noise, and a
   * burst of reorders would stack them. The move MODAL has no such feedback
   * and keeps the toast.
   */
  silent?: boolean
}

/**
 * Moves a team into another division. The team's roster follows it; ordering
 * within the destination is caller-controlled via `placement` — omit it and the
 * server appends the team.
 *
 * The tree query is invalidated on SETTLE rather than on success, so a failed
 * move refetches the true server state instead of leaving the optimistic one
 * on screen.
 */
export const useMoveTeam = (rootScheduleId: string) => {
  'use memo'

  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      teamId,
      fromScheduleId,
      toScheduleId,
      placement,
    }: MoveArgs) => {
      // `seed_action` is always sent: the API requires the field even when
      // there is no neighbouring team to anchor against.
      const body = {
        destination_schedule_id: toScheduleId,
        seed_action: placement?.seed_action ?? 'after',
        ...(placement?.destination_team_id
          ? { destination_team_id: placement.destination_team_id }
          : {}),
      }

      await req(ENDPOINTS.SCHEDULES.MOVE_TEAM(fromScheduleId, teamId), {
        method: 'PUT',
        // `req` does not serialise — an object would go out as the literal
        // string "[object Object]".
        body: JSON.stringify(body),
      })
    },
    onSuccess: (_data, { silent }) => {
      if (silent) {
        return
      }
      message.success(
        translate('Web:xEditedSuccessfully', { x: translate('common:team') })
      )
    },
    onError: (error: Error) => {
      message.error(getErrorMessage(error))
    },
    // Returned, not fire-and-forget: `mutateAsync` then resolves only once
    // both refetches land. Callers that re-select the moved team depend on it —
    // the team's node id changes with its parent, and selecting the new id
    // before the tree holds it unmounts the panel mid-flight.
    onSettled: (_data, _error, { teamId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.operations.tree(rootScheduleId),
        }),
        // The team's ROSTER list must go too. It is keyed by team id only,
        // which a move does not change, so without this React Query serves the
        // cached list — which still maps this team to its old division. The
        // panel then resolves no roster for the new division and never issues
        // a request at all.
        queryClient.invalidateQueries({
          queryKey: queryKeys.operations.teamRosters(teamId),
        }),
        // The Team page caches the same endpoint's raw list under its own key;
        // the move changes that list too.
        queryClient.invalidateQueries({
          queryKey: queryKeys.teamRosters.byTeam(teamId),
        }),
      ]),
  })

  return {
    moveTeam: (args: MoveArgs) => mutation.mutateAsync(args),
    isMoving: mutation.isPending,
  }
}
